import {
  Box,
  GridLayout,
  GridLayoutColumn,
  IconButton,
  TextButton,
  Text,
  Flex,
} from '@updater/ui-uds';
import styled from '@emotion/styled';
import {
  NoPlansMatchResetFilters,
  PageHeading,
  ProductTypes,
  SortMethod,
  useFilterAndSortOffers,
} from '@updater/ui-widgets';

import { useRouter } from 'next/router';
import { ArrowLeft, MapPin } from '@phosphor-icons/react';
import {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';

import { OffersContext, MicrositeContext } from 'context';
import { ShopType, SiteMode } from 'types';
import {
  LoadingDisplay,
  PlanProviderCards,
  CrossSellModal,
  UpdaterCrossSellModal,
  NotServiceableModal,
  AsyncLoadingModal,
  ProviderRedirectModal,
  CallCenterInfo,
} from 'components';
import { useIsZipOnlyAddress, useProviderRedirect, useStore } from 'hooks';
import { Events, useTracking } from 'services';
import { ServiceabilityProvider } from '@updater/consumer-graph';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTermGroupedOffers } from 'hooks/useTermGroupedOffers';
import { PILL_TO_SORT_METHOD, SortPill } from 'constants/sortPills';
import { PROVIDER_APP_SHOW_TFN_IN_HEADER } from 'constants/experiments';
import { TermsAcceptanceFooter } from './TermsAcceptanceFooter';
import { ResultsPhoneCta } from './ResultsPhoneCta';

const AddressContainer = styled(Flex)`
  place-items: center;
`;

/** Main component for Plan Page */
export const PlansPage: FC = () => {
  const showPhoneCta = useFeatureIsOn(PROVIDER_APP_SHOW_TFN_IN_HEADER.NAME);
  const isActiveServiceFeatureEnabled = useFeatureIsOn(
    'provider_app_active_service_enabled'
  );
  const [activePill, setActivePill] = useState<SortPill>(SortPill.Recommended);
  const offersContext = useContext(OffersContext);
  const micrositeConfig = useContext(MicrositeContext);
  const router = useRouter();
  const tracker = useTracking();
  const [setWantsNewAddress, siteMode, reskinProvider, hideChrome] = useStore(
    (store) => [
      store.setWantsNewAddress,
      store.siteMode,
      store.reskinProvider,
      store.hideChrome,
    ]
  );
  const hideHeaderAndFooter =
    hideChrome || micrositeConfig?.iFrameConfig?.hideHeaderAndFooter;
  const [setAcceptedUpdaterTerms] = useStore(
    useShallow((store) => [store.setAcceptedUpdaterTerms])
  );
  const { primaryData, secondaryData, tertiaryData } = offersContext;
  const isZipOnlyAddress = useIsZipOnlyAddress();

  // Reskin logic: if this is a single provider site, and the primary provider
  // is not available, AND there are offers from another provider of the same
  // type (either MSO or Telco), we reskin provider app to look like the
  // secondary provider.  We also need to show offers from that secondary
  // provider.  When the reskin completes, we also change the site mode.
  // Load our state, including the offers, from either the primaryData payload
  // or the secondaryData payload depending on the SiteMode.  Note:
  // * there's a small chance that multiple providers will have offers in the secondaryData
  //   payload.  This is OK -- the site will look like one of them but we can still
  //   show multiple providers
  // * for multi-provider sites, the siteMode will never be anything but DEFAULT_MODE,
  //   so it is safe to make this check here without explicitly checking the
  //   microsite config
  const dataSource =
    siteMode === SiteMode.DEFAULT_MODE ? primaryData : secondaryData;
  const {
    address,
    loading,
    availableProviders,
    offersWithProviders,
    productTypes,
    requestId,
  } = dataSource;

  const hasOffers = offersWithProviders && offersWithProviders.length > 0;

  const reskinFilteredOffers = useMemo(() => {
    if (siteMode === SiteMode.RESKIN_MODE && reskinProvider) {
      return offersWithProviders?.filter(
        (entry) => entry?.provider?.code === reskinProvider.providerCode
      );
    }
    return offersWithProviders;
  }, [siteMode, reskinProvider, offersWithProviders]);

  const changeAddressHandler = () => setWantsNewAddress(true);

  const offersLoadTime = useRef(new Date().getTime());
  useEffect(() => {
    if (loading) {
      offersLoadTime.current = new Date().getTime();
    } else {
      const waitTime = new Date().getTime() - offersLoadTime.current;
      tracker.trackEvent(Events.SEARCH_OFFERS_WAIT_TIME, 'measured', {
        waitTime,
      });
    }
  }, [loading, tracker]);

  // The quiz asks about no services, so nothing it collects says which plans
  // to show. Leaving the filter open also keeps the visitor off "no plans
  // match", which the page renders no controls to undo (productionized
  // feature).
  const defaultProductFilter = useMemo(() => [], []);
  const {
    setAvailableProviders,
    setProductTypes,
    updateSortOrder,
    offers: filteredSortedOffers,
    allOffers,
  } = useFilterAndSortOffers({
    defaultSortMethod: SortMethod.PriceLowToHigh,
    defaultSelections: {
      products: defaultProductFilter,
      providers: [],
      additionalLanguages: [],
    },
    offersWithProviders: reskinFilteredOffers,
  });

  const { offers: termGroupedOffers, isFeatureLoading: termGroupingLoading } =
    useTermGroupedOffers(filteredSortedOffers);

  useEffect(() => {
    const filtered =
      siteMode === SiteMode.RESKIN_MODE && reskinProvider
        ? (availableProviders as ServiceabilityProvider[])?.filter(
            (p) => p.code === reskinProvider.providerCode
          )
        : (availableProviders as ServiceabilityProvider[]);
    setAvailableProviders(filtered);
  }, [siteMode, reskinProvider, availableProviders]);

  setProductTypes(productTypes as ProductTypes[]);
  const isSingleProviderSite =
    micrositeConfig.shopType === ShopType.SINGLE_PROVIDER;

  const isActiveService =
    isActiveServiceFeatureEnabled &&
    hasOffers &&
    offersWithProviders[0].offer?.tags?.find(
      (tag: string) => tag === 'active-service'
    );

  const updatedCheckoutType =
    isActiveService && micrositeConfig.activeServiceCheckoutType
      ? micrositeConfig.activeServiceCheckoutType
      : micrositeConfig.defaultCheckoutType;

  // Resolved before showLoadingScreen because the loading gate depends on it.
  const { redirectEnabled, redirectPending } =
    useProviderRedirect(updatedCheckoutType);

  // This should be readable but the logic for the loading screen:
  // (1) if it's multi-provider site, we only look at the primaryData
  // since we're requesting all providers at once;
  // (2) if it's a single provider site, we have to load the primary
  // and the secondary data so we can handle the cross-sell modal.
  // Additionally, if this microsite has updater cross-sell providers,
  // we must also wait for the tertiary data.  The LoadingDisplay
  // component will change state if we're waiting only for the tertiaryData,
  // so this component only needs to know whether the LoadingDisplay
  // should show or not.
  const showLoadingScreen = useMemo(() => {
    let showLoading = false;
    if (siteMode === SiteMode.DEFAULT_MODE) {
      if (isSingleProviderSite) {
        if (primaryData?.loading) {
          showLoading = true;
        } else {
          // if we have primary offers to show, we don't need to wait
          // for the secondaryData to load
          if (!hasOffers) {
            if (secondaryData?.loading) {
              showLoading = true;
            } else if (
              tertiaryData?.loading &&
              micrositeConfig.updaterCrossSellActive
            ) {
              showLoading = true;
            }
          }
        }
      } else {
        showLoading = primaryData.loading;
      }
    } else {
      // this should never fire?
    }
    // Offers can resolve before the feature-flag SDK does. Without this the
    // cards paint, then vanish when the gate lands and the redirect takes over.
    if (redirectPending) {
      showLoading = true;
    }
    return showLoading;
  }, [
    hasOffers,
    isSingleProviderSite,
    micrositeConfig.updaterCrossSellActive,
    primaryData?.loading,
    redirectPending,
    secondaryData?.loading,
    siteMode,
    tertiaryData?.loading,
  ]);

  // Replace with full address page if zip-only qual and no offers
  useEffect(() => {
    if (isZipOnlyAddress && !loading && !hasOffers) {
      router.replace('/full-address');
    }
  }, [isZipOnlyAddress, loading, hasOffers, router]);

  // Track page view
  useEffect(() => {
    if (showLoadingScreen) {
      tracker.trackEvent(Events.OFFERS_LOADING, 'viewed');
    } else {
      tracker.trackEvent(Events.PLANS, 'viewed');
    }
  }, [showLoadingScreen, tracker]);

  if (showLoadingScreen) {
    return <LoadingDisplay />;
  }

  const formattedAddress =
    !isZipOnlyAddress && address
      ? `${address?.street}, ${address?.unit ? `${address?.unit},` : ''} ${
          address?.city
        },  ${address?.state} ${address?.postalCode}`
      : '';

  // Microsites where we can't sell directly should be redirected to
  // the provider site via the ProviderRedirectModal.  We also want to
  // suppress showing the offers here in case something gets borked
  // with the redirect.
  // Keyed off redirectEnabled rather than the checkout type alone: a gated or
  // url-less redirect never opens the modal, and suppressing offers anyway
  // would leave the user with a blank page instead of /plans.
  const suppressOffers = redirectEnabled && siteMode === SiteMode.DEFAULT_MODE;

  const onBackClick = () => {
    setAcceptedUpdaterTerms(false);
    router.back();
  };

  // Skipping straight to Plans makes the offers page the effective first
  // page — router.back() has nothing useful in history, so hide the button.
  const skippedToPlans = router.isReady && Boolean(router.query.skipToPlans);

  const onSortPillChange = (pill: SortPill) => {
    setActivePill(pill);
    updateSortOrder(PILL_TO_SORT_METHOD[pill]);
  };

  // With the flag on, the title shares its row with the call center CTA,
  // at the right edge of the page.
  const withPhoneCta = (title: ReactNode) =>
    showPhoneCta ? (
      <Flex
        alignItems="center"
        justifyContent="space-between"
        data-testid="plans-title-row"
      >
        {title}
        <Box flexShrink={0} marginLeft="s">
          <ResultsPhoneCta />
        </Box>
      </Flex>
    ) : (
      title
    );

  return (
    <Box pt="xxl" pb={hideHeaderAndFooter ? undefined : 'm'} width="100%">
      {!skippedToPlans && (
        <IconButton
          onClick={() => onBackClick()}
          size="s"
          icon={<ArrowLeft />}
          variant="secondary"
          type="button"
          aria-label="Back button"
          data-cy="backButton"
          data-testid="back-button"
        >
          Back
        </IconButton>
      )}
      <GridLayout gridGap="m" marginTop="m" includeMargin={false}>
        {/* Title */}
        <GridLayoutColumn
          span={
            showPhoneCta
              ? ['4', '4', '4', '12', '12']
              : ['4', '4', '4', '8', '8']
          }
          spanStart="1"
          maxWidth="100%"
        >
          <Box>
            {isZipOnlyAddress ? (
              withPhoneCta(
                <Box marginTop="s">
                  <PageHeading>Results for {address.postalCode}</PageHeading>
                </Box>
              )
            ) : (
              <>
                {withPhoneCta(
                  <Box marginY="s">
                    <PageHeading>Results</PageHeading>
                  </Box>
                )}
                {formattedAddress && (
                  <AddressContainer>
                    <Box>
                      <MapPin size="20px" />
                    </Box>
                    <Text
                      variant={['m', null, 'l', null, null]}
                      marginX="s"
                      className="formattedAddress"
                      data-testid="formatted-address"
                      style={{
                        textWrap: 'balance',
                      }}
                    >
                      {formattedAddress}
                    </Text>
                    <TextButton onClick={changeAddressHandler}>
                      Change
                    </TextButton>
                  </AddressContainer>
                )}
              </>
            )}
          </Box>
        </GridLayoutColumn>
        {hasOffers && !suppressOffers && (
          <>
            {/* Full-width results, no filter sidebar (productionized feature). */}
            <GridLayoutColumn
              span={['4', '4', '4', '12', '12']}
              spanStart="1"
              // Full width, so the hero carousel can bleed into the page padding.
              minWidth={0}
              maxWidth="100%"
            >
              <NoPlansMatchResetFilters
                filteredSortedOffers={filteredSortedOffers}
                allOffers={allOffers}
              />
              <PlanProviderCards
                requestId={requestId}
                filteredSortedOffers={termGroupedOffers}
                termGroupingLoading={termGroupingLoading}
                activePill={activePill}
                onSortPillChange={onSortPillChange}
                aside={<CallCenterInfo />}
              />
            </GridLayoutColumn>
          </>
        )}
        {!hasOffers && !suppressOffers && (
          <GridLayoutColumn span={['12', '12', '8', '8', '9']} spanStart="1">
            <Text as="h2" variant="mBold">
              We were unable to find offers at the given address.
            </Text>
            <CallCenterInfo mt="m" />
          </GridLayoutColumn>
        )}
      </GridLayout>
      {isSingleProviderSite && (
        <>
          <CrossSellModal />
          <ProviderRedirectModal updatedCheckoutType={updatedCheckoutType} />
          <UpdaterCrossSellModal />
        </>
      )}
      <NotServiceableModal />
      <AsyncLoadingModal />
      <TermsAcceptanceFooter />
    </Box>
  );
};
